/// <amd-module name="Repos/VersionDialogs/VersionDialogsService" />

import {
    GitAnnotatedTag,
    GitObject,
    GitRefUpdate,
    GitRefUpdateResult,
    GitRefUpdateStatus,
    GitRepository,
    GitVersionDescriptor,
    GitVersionType
} from "Repos/Common/Contracts/Git";
import { branchNameToRef, EMPTY_OBJECT_ID, refToBranchName, refToTagName, specToRefName, trimRefs } from "Repos/Common/Util/Ref";
import * as Resources from "Repos/VersionDialogs/Resources";
import { IObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { getGitClient } from "./RestClient/Git";

export interface DialogResult {
    item?: any;
    error?: Error;
    inProgress?: boolean;
}

export interface LinkingError {
    error?: Error;
    branch?: GitRefUpdateResult;
}

export interface IVersionDialogContext {
    dialogResult: IObservableValue<DialogResult>;
    repository: GitRepository;
}
export interface IVersionDialogWithLinkingContext extends IVersionDialogContext {
    linkingError: IObservableValue<LinkingError>;
}

export interface IVersionDialogsService extends IVssService {
    createBranch: (
        dialogContext: IVersionDialogWithLinkingContext,
        branchName: string,
        version: GitVersionDescriptor,
        linkingPromise?: Promise<void>
    ) => void;
    createTag: (dialogContext: IVersionDialogContext, tagName: string, version: GitVersionDescriptor, description: string) => void;
    deleteBranch: (dialogContext: IVersionDialogContext, branchVersion: GitVersionDescriptor) => void;
    deleteTag: (dialogContext: IVersionDialogContext, tagVersion: GitVersionDescriptor) => void;
}

const branchDeleteStatusToMessage: { [key: number]: string } = {
    [GitRefUpdateStatus.ForcePushRequired]: Resources.ErrorDeleteBranchForcePushRequired,
    [GitRefUpdateStatus.RejectedByPolicy]: Resources.ErrorDeleteBranchRejectedByPolicy
};

class VersionDialogsService extends VssService implements IVersionDialogsService {
    public createBranch(
        context: IVersionDialogWithLinkingContext,
        branchName: string,
        version: GitVersionDescriptor,
        linkingPromise?: Promise<void>
    ): void {
        const { dialogResult, linkingError, repository } = context;

        // Mark result as in progress
        dialogResult.value = { inProgress: true };

        this.getObjectId(repository, version, true)
            .then(newObjectId => {
                const newBranch = {
                    name: branchNameToRef(branchName),
                    newObjectId,
                    oldObjectId: EMPTY_OBJECT_ID
                } as GitRefUpdate;

                // Perform actual create operation
                return getGitClient(this.pageContext).updateRefs([newBranch], repository.id, repository.project.id);
            })
            .then(result => this.getCreateRefResult(result, GitVersionType.Branch))
            .then(result =>
                // If there is any linked work items, perform the linking
                linkingPromise
                    ? linkingPromise.then(
                          () => result,
                          error => {
                              // If there is an error during linking we inform the customer that
                              // the branch is created but the links couldn't be added due to an error.
                              // Dialog will stay open with showing only error message and close button.
                              const linkingError = new Error(error.message);
                              linkingError.name = "LinkingError";
                              (linkingError as any).branch = result;
                              throw linkingError;
                          }
                      )
                    : result
            )
            .then(item => (dialogResult.value = { item }))
            .catch(error => {
                if (error.name === "LinkingError") {
                    linkingError.value = { branch: error.branch, error };
                } else {
                    dialogResult.value = { error };
                }
            });
    }

    public createTag(context: IVersionDialogContext, name: string, version: GitVersionDescriptor, message: string): void {
        const { dialogResult, repository } = context;

        // Mark result as in progress
        dialogResult.value = { inProgress: true };

        this.getObjectId(repository, version, true)
            .then(objectId => {
                const taggedObject = { objectId } as GitObject;
                const newTag = { message, name, taggedObject } as GitAnnotatedTag;
                return getGitClient(this.pageContext).createAnnotatedTag(newTag, repository.project.id, repository.id);
            })
            .then(item => (dialogResult.value = { item }))
            .catch(error => (dialogResult.value = { error }));
    }

    public deleteBranch(dialogContext: IVersionDialogContext, branchVersion: GitVersionDescriptor): void {
        this.deleteRef(dialogContext, branchVersion, name => `refs/heads/${name}`);
    }

    public deleteTag(dialogContext: IVersionDialogContext, tagVersion: GitVersionDescriptor): void {
        this.deleteRef(dialogContext, tagVersion, name => `refs/tags/${name}`);
    }

    private async getObjectId(repository: GitRepository, version: GitVersionDescriptor, peelTags: boolean = false): Promise<string> {
        let objectIdPromise: Promise<string>;
        if (version.versionType === GitVersionType.Branch || version.versionType === GitVersionType.Tag) {
            // We need to get the object id first for branch and tag
            objectIdPromise = getGitClient(this.pageContext)
                .getRefs(repository.id, repository.project.id, trimRefs(specToRefName(version)), undefined, undefined, undefined, undefined, peelTags)
                .then(refs => {
                    if (refs.length < 1) {
                        throw format(Resources.RefFindError, version.version);
                    }

                    return refs[0].peeledObjectId || refs[0].objectId;
                });
        } else {
            // Version is the object id for the commit
            objectIdPromise = Promise.resolve(version.version);
        }

        return objectIdPromise;
    }

    private deleteRef(dialogContext: IVersionDialogContext, refDescriptor: GitVersionDescriptor, nameToRefDelete: (name: string) => string): void {
        const { dialogResult, repository } = dialogContext;

        // Mark result as in progress
        dialogResult.value = { inProgress: true };

        this.getObjectId(repository, refDescriptor)
            .then(objectId => {
                const refToDelete = {
                    name: nameToRefDelete(refDescriptor.version),
                    newObjectId: EMPTY_OBJECT_ID,
                    oldObjectId: objectId
                } as GitRefUpdate;

                return getGitClient(this.pageContext).updateRefs([refToDelete], repository.id, repository.project.id);
            })
            .then(result => this.getDeleteRefResult(result, refDescriptor.versionType))
            .then(result => (dialogResult.value = { item: result }))
            .catch(error => (dialogResult.value = { error }));
    }

    private getCreateRefResult(updateResults: GitRefUpdateResult[], versionType: GitVersionType): GitRefUpdateResult {
        const updateResult = updateResults[0];
        if (updateResult.updateStatus !== GitRefUpdateStatus.Succeeded) {
            throw new Error(updateResult.customMessage || format(Resources.ErrorCreateFailed, this.getShortRefName(updateResult.name, versionType)));
        }

        return updateResult;
    }

    private getDeleteRefResult(updateResults: GitRefUpdateResult[], versionType: GitVersionType): GitRefUpdateResult {
        const updateResult = updateResults[0];
        if (updateResult.updateStatus !== GitRefUpdateStatus.Succeeded) {
            let errorMessage: string | undefined = undefined;
            const targetName = this.getShortRefName(updateResult.name, versionType);

            if (updateResult.customMessage) {
                errorMessage = updateResult.customMessage;
            } else if (versionType === GitVersionType.Branch) {
                const formatString = branchDeleteStatusToMessage[updateResult.updateStatus];
                errorMessage = formatString && format(formatString, targetName);
            }

            throw new Error(errorMessage || format(Resources.ErrorDeleteFailed, targetName));
        }
        return updateResult;
    }

    private getShortRefName(name: string, versionType: GitVersionType) {
        switch (versionType) {
            case GitVersionType.Branch:
                return refToBranchName(name);
            case GitVersionType.Commit:
                return name;
            case GitVersionType.Tag:
                return refToTagName(name);
        }
    }
}

const serviceName = "IVersionDialogsService";
Services.add(serviceName, { serviceFactory: VersionDialogsService });

export function getService(pageContext: IVssPageContext): IVersionDialogsService {
    return pageContext.getService<IVersionDialogsService>(serviceName);
}
